import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SaleProductItem } from '@/api/saleApi';

interface ProductItemSelectorModalProps {
  productLabel: string;
  items: SaleProductItem[];
  selectedItemIds: number[];
  blockedItemIds: number[];
  maxSelection: number;
  onClose: () => void;
  onConfirm: (items: SaleProductItem[]) => void;
}

const ProductItemSelectorModal: React.FC<ProductItemSelectorModalProps> = ({
  productLabel,
  items,
  selectedItemIds,
  blockedItemIds,
  maxSelection,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [draftIds, setDraftIds] = useState<number[]>(selectedItemIds);
  const [error, setError] = useState('');
  const blockedIds = useMemo(() => new Set(blockedItemIds), [blockedItemIds]);

  useEffect(() => {
    setDraftIds(selectedItemIds);
    setError('');
  }, [selectedItemIds]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  const toggleItem = (itemId: number) => {
    if (blockedIds.has(itemId)) return;
    setDraftIds((currentIds) => {
      if (currentIds.includes(itemId)) {
        setError('');
        return currentIds.filter((id) => id !== itemId);
      }
      if (currentIds.length >= maxSelection) {
        setError(t('admin.sales.selector.max_error', { count: maxSelection }));
        return currentIds;
      }
      setError('');
      return [...currentIds, itemId];
    });
  };

  const confirmSelection = () => {
    if (draftIds.length === 0) {
      setError(t('admin.sales.selector.required_error'));
      return;
    }
    const selectedIds = new Set(draftIds);
    onConfirm(items.filter((item) => selectedIds.has(item.id)));
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 p-3 sm:p-5"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-[20px] border border-outline-variant bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-item-selector-title"
      >
        <header className="flex items-start justify-between gap-3 border-b border-outline-variant px-4 py-4 sm:px-5">
          <div className="min-w-0">
            <h2 id="product-item-selector-title" className="font-h2 text-h2 text-on-surface">
              {t('admin.sales.selector.title')}
            </h2>
            <p className="mt-1 truncate text-body-sm text-secondary">{productLabel}</p>
          </div>
          <button
            type="button"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-secondary hover:bg-surface-container-low"
            aria-label={t('admin.sales.selector.close')}
            onClick={onClose}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">
          <p className="mb-3 text-body-sm text-secondary">
            {t('admin.sales.selector.summary', {
              selected: draftIds.length,
              maximum: maxSelection,
            })}
          </p>
          <div className="space-y-2">
            {items.map((item) => {
              const isBlocked = blockedIds.has(item.id);
              const isChecked = draftIds.includes(item.id);
              return (
                <label
                  key={item.id}
                  className={`flex items-start gap-3 rounded-xl border px-3 py-3 transition-colors ${
                    isBlocked
                      ? 'cursor-not-allowed border-outline-variant bg-surface-container-low opacity-60'
                      : isChecked
                        ? 'cursor-pointer border-primary bg-primary-fixed/40'
                        : 'cursor-pointer border-outline-variant hover:border-primary/60'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 accent-primary"
                    checked={isChecked}
                    disabled={isBlocked}
                    onChange={() => toggleItem(item.id)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block break-all font-label-md text-on-surface">
                      IMEI: {item.imei || '-'}
                    </span>
                    <span className="mt-0.5 block break-all text-body-sm text-secondary">
                      Serial: {item.serialNumber || '-'}
                    </span>
                    {isBlocked && (
                      <span className="mt-1 block text-body-sm text-error">
                        {t('admin.sales.selector.blocked')}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
          {error && <p className="mt-3 text-body-sm text-error" role="alert">{error}</p>}
        </div>

        <footer className="flex flex-col-reverse gap-2 border-t border-outline-variant bg-surface-lowest px-4 py-4 sm:flex-row sm:justify-end sm:px-5">
          <button
            type="button"
            className="min-h-10 rounded border border-outline-variant bg-white px-4 font-label-md text-secondary hover:bg-surface-container-low"
            onClick={onClose}
          >
            {t('admin.sales.selector.skip')}
          </button>
          <button
            type="button"
            className="min-h-10 rounded bg-primary px-4 font-label-md text-on-primary hover:bg-primary-fixed-variant"
            onClick={confirmSelection}
          >
            {t('admin.sales.selector.confirm', { count: draftIds.length })}
          </button>
        </footer>
      </section>
    </div>
  );
};

export default ProductItemSelectorModal;
